"use client";

import { c as _c } from "react/compiler-runtime";
import { Item, ItemAvatar, ItemContent } from "@opensea/next-ui-kit";
import { ChartSkeleton, classNames, SkeletonBlock, SkeletonLine, TableCell, TableRow, tableRowSizeVariants, useIsLessThanMd, useTable } from "@opensea/ui-kit";
import { CollectionWatchlistIcon } from "@/components/collection/CollectionWatchlistButton/CollectionWatchlistIcon";
import { DEFAULT_STATS_TIMEFRAME } from "@/hooks/query-param/use-stats-timeframe-query-param";
import { useStatsColumns } from "../use-stats-columns";
import { useStatsSparklineChartClassName } from "../use-stats-sparkline-chart-classname";
import { STATS_TABLE_COLUMN_CLASSNAMES } from "./constants";
export function StatsTableRowSkeleton(t0) {
  const $ = _c(42);
  const {
    context,
    override
  } = t0;
  const columns = useStatsColumns(DEFAULT_STATS_TIMEFRAME, context?.portfolio ?? false);
  const {
    size
  } = useTable();
  const {
    image
  } = tableRowSizeVariants({
    size
  });
  const isLessThanMd = useIsLessThanMd();
  const skeletonClassName = override?.Skeleton?.className || "";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.watchlist}><CollectionWatchlistIcon loading={true} /></TableCell>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const watchlist = t1;
  const sparklineClassName = useStatsSparklineChartClassName();
  const T0 = TableRow;
  const t2 = isLessThanMd ? null : watchlist;
  const T1 = TableCell;
  const t3 = STATS_TABLE_COLUMN_CLASSNAMES;
  const T2 = Item;
  const t4 = "unstyled";
  const T3 = ItemAvatar;
  const t5 = image();
  let t6;
  if ($[1] !== skeletonClassName) {
    t6 = classNames(skeletonClassName);
    $[1] = skeletonClassName;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== t6) {
    t7 = <SkeletonBlock className={t6} />;
    $[3] = t6;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== T3 || $[6] !== t5 || $[7] !== t7) {
    t8 = <T3 className={t5}>{t7}</T3>;
    $[5] = T3;
    $[6] = t5;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== skeletonClassName) {
    t9 = classNames("h-4 w-[100px]", skeletonClassName);
    $[9] = skeletonClassName;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== t9) {
    t10 = <ItemContent><SkeletonLine className={t9} /></ItemContent>;
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== T2 || $[14] !== t10 || $[15] !== t8) {
    t11 = <T2 variant={t4}>{t8}{t10}</T2>;
    $[13] = T2;
    $[14] = t10;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== T1 || $[18] !== t11 || $[19] !== t3.collection) {
    t12 = <T1 className={t3.collection}>{t11}</T1>;
    $[17] = T1;
    $[18] = t11;
    $[19] = t3.collection;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== context?.portfolio || $[22] !== skeletonClassName) {
    t13 = context?.portfolio ? <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.held}><SkeletonLine className={classNames("h-4 w-[80px]", skeletonClassName)} /></TableCell> : null;
    $[21] = context?.portfolio;
    $[22] = skeletonClassName;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== columns || $[25] !== skeletonClassName) {
    let t15;
    if ($[27] !== skeletonClassName) {
      t15 = column => <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.stat} key={column.key}><SkeletonLine className={classNames("h-4 w-[88px]", skeletonClassName)} /></TableCell>;
      $[27] = skeletonClassName;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    t14 = columns.map(t15);
    $[24] = columns;
    $[25] = skeletonClassName;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[29] !== skeletonClassName || $[30] !== sparklineClassName) {
    t15 = classNames(sparklineClassName, skeletonClassName);
    $[29] = skeletonClassName;
    $[30] = sparklineClassName;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15) {
    t16 = <TableCell className={STATS_TABLE_COLUMN_CLASSNAMES.last7Days}><ChartSkeleton className={t15} /></TableCell>;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const t17 = isLessThanMd ? watchlist : null;
  let t18;
  if ($[34] !== T0 || $[35] !== t12 || $[36] !== t13 || $[37] !== t14 || $[38] !== t16 || $[39] !== t17 || $[40] !== t2) {
    t18 = <T0>{t2}{t12}{t13}{t14}{t16}{t17}</T0>;
    $[34] = T0;
    $[35] = t12;
    $[36] = t13;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
    $[40] = t2;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
}